import { add } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { User } from "../../core/providers/auth";
import { Workout } from "../../providers/workouts";
import { FormInputs, initWorkout } from "../../reducers/workoutReducer";
import { PublicUser } from "../../providers/users";

export type InputTypes =
  | number
  | Date
  | string
  | string[]
  | PublicUser[]
  | null;

export type FormInput = {
  error: boolean;
  input: InputTypes;
  message: string;
};

export type WorkoutFormModel = {
  [key in FormInputs]: FormInput;
};

export type RecurrenceEditScope = "single" | "following";

export const updateWorkout = (
  workoutForm: WorkoutFormModel,
  workout: Workout
): Workout => {
  insertFormInputs(workoutForm, workout);
  return workout;
};

export const createWorkout = (
  workoutForm: WorkoutFormModel,
  user: User,
  id: string
): Workout => {
  const workout = {
    ...initWorkout,
    admin: { ...initWorkout.admin },
    gear: [...initWorkout.gear],
    participantsList: [],
    waitlist: [],
    waitlistCount: 0,
    coaches: [],
  };
  workout.id = id;
  insertFormInputs(workoutForm, workout);
  workout.admin = {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    phone: user.phoneNumber,
    photoURL: user.photoURL,
  };
  return workout;
};

export const createDate = (date: Date, time: Date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds()
  );
};

export const createDayDate = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

type InsertFormInputOptions = {
  preserveParticipantsCount?: boolean;
};

export const applyWorkoutFormToWorkout = (
  workoutForm: WorkoutFormModel,
  workout: Workout,
  options: InsertFormInputOptions = {}
) => {
  const { preserveParticipantsCount = false } = options;
  const selectedDate = workoutForm[FormInputs.date].input as Date;
  const selectedStartTime = workoutForm[FormInputs.startTime].input as Date;
  const selectedEndTime = workoutForm[FormInputs.endTime].input as Date;
  const normalizedStartTime = createDate(selectedDate, selectedStartTime);
  const normalizedEndTime = createDate(selectedDate, selectedEndTime);

  workout.type = workoutForm[FormInputs.type].input as string;
  workout.body = workoutForm[FormInputs.body].input as string;
  workout.gear = workoutForm[FormInputs.gear].input as string[];
  workout.coaches = workoutForm[FormInputs.coaches].input as PublicUser[];
  workout.numberOfPlaces = workoutForm[FormInputs.numberOfPlaces]
    .input as number;
  if (!preserveParticipantsCount) {
    workout.participantsCount = workoutForm[FormInputs.participantsCount]
      .input as number;
  }
  workout.startTime = Timestamp.fromDate(normalizedStartTime);
  workout.endTime = Timestamp.fromDate(normalizedEndTime);
  workout.location = workoutForm[FormInputs.location].input as string;
  workout.collectionTime = workoutForm[FormInputs.collectionTime]
    .input as number;
  workout.registrationTime = workoutForm[FormInputs.registrationTime]
    .input as number;
  workout.date = Timestamp.fromDate(createDayDate(selectedDate));
  workout.credits = workoutForm[FormInputs.credits].input as number;
};

const insertFormInputs = (workoutForm: WorkoutFormModel, workout: Workout) => {
  applyWorkoutFormToWorkout(workoutForm, workout);
};

export const getInitWorkoutFormModel = (
  workout?: Workout
): WorkoutFormModel => {
  const workoutForm: any = {};
  workoutForm[FormInputs.type] = {
    error: false,
    input: workout?.type ?? "",
    message: "",
  };
  workoutForm[FormInputs.body] = {
    error: false,
    input: workout?.body ?? "",
    message: "",
  };
  workoutForm[FormInputs.gear] = {
    error: false,
    input: workout?.gear ?? [],
    message: "",
  };
  workoutForm[FormInputs.coaches] = {
    error: false,
    input: workout?.coaches ?? [],
    message: "",
  };
  workoutForm[FormInputs.date] = {
    error: false,
    input: workout?.date ? workout?.date.toDate() : new Date(),
    message: "",
  };
  workoutForm[FormInputs.numberOfPlaces] = {
    error: false,
    input: workout?.numberOfPlaces ?? 0,
    message: "",
  };
  workoutForm[FormInputs.participantsCount] = {
    error: false,
    input: workout?.participantsCount ?? 0,
    message: "",
  };
  workoutForm[FormInputs.startTime] = {
    error: false,
    input: workout?.startTime.toDate() ?? new Date(),
    message: "",
  };
  workoutForm[FormInputs.endTime] = {
    error: false,
    input: workout?.endTime.toDate() ?? add(new Date(), { hours: 1 }),
    message: "",
  };
  workoutForm[FormInputs.location] = {
    error: false,
    input: workout?.location ?? "",
    message: "",
  };
  workoutForm[FormInputs.credits] = {
    error: false,
    input: workout?.credits ?? 0,
    message: "",
  };
  workoutForm[FormInputs.collectionTime] = {
    error: false,
    input: workout?.collectionTime ?? 0,
    message: "",
  };
  workoutForm[FormInputs.registrationTime] = {
    error: false,
    input: workout?.registrationTime ?? 0,
    message: "",
  };

  return workoutForm as WorkoutFormModel;
};
